import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { ACTION_ICONS } from "../../config/siteConfig";
import { DOMAIN_URL } from "../../config/apiConfig";
import ContentLoader from "../../components/loader/ContentLoader";
import DetailView from "../../components/location/DetailView";
import { offersService } from "../../services/offers.service";
import { commonService } from "../../services/common.service";
import { formatDateYYYYMMDD } from "../../utils/dateUtils";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "expired", label: "Expired" },
];

const AUDIENCE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "agent", label: "Agent" },
  { value: "supplier", label: "Supplier" },
];

const DISCOUNT_TYPE_OPTIONS = [
  { value: "percentage", label: "Percentage (%)" },
  { value: "fixed", label: "Fixed Amount" },
];

const STATUS_STYLES = {
  active: { bg: "#d4edda", color: "#155724" },
  inactive: { bg: "#f8d7da", color: "#721c24" },
  expired: { bg: "#fff3cd", color: "#856404" },
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const initialFilters = { search: "", audience: "", status: "" };

const initialOffer = {
  title: "",
  promo_code: "",
  description: "",
  terms_conditions: "",
  img_url: "",
  discount_type: "percentage",
  discount_value: "",
  max_discount_amount: "",
  min_booking_amount: "",
  valid_from: "",
  valid_to: "",
  audience: "all",
  display_order: "0",
  status: "active",
};

const labelOf = (v) => (v ? String(v).replace(/_/g, " ").toUpperCase() : "-");
const formatDate = (v) => (v ? moment(v).format("DD MMM YYYY") : "-");
const formatDateTime = (v) => (v ? moment(v).format("DD MMM YYYY, hh:mm A") : "-");
const toPickerDate = (v) => (v ? moment(v).format("DD-MM-YYYY") : "");
const toNumberOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// img_url comes back relative to the API host, so prefix DOMAIN_URL (absolute urls are left alone)
const imageSrc = (u) => (u ? (/^https?:\/\//i.test(u) ? u : `${DOMAIN_URL}${String(u).replace(/^\/+/, "")}`) : "");

const money = (v) => (v !== null && v !== undefined && v !== "" ? `₹ ${v}` : "-");

const discountText = (o) =>
  o?.discount_type === "percentage"
    ? `${Number(o?.discount_value) || 0}%`
    : `₹ ${Number(o?.discount_value) || 0}`;

const Offers = () => {
  const { start, complete } = useLoadingBar();

  const [loading, setLoading] = useState(false);
  const [offerRes, setOfferRes] = useState({ pagination: {}, offers: [] });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  // popup state: formMode is "add" | "edit" | null
  const [formMode, setFormMode] = useState(null);
  const [editId, setEditId] = useState(null);
  const [offer, setOffer] = useState(initialOffer);
  const [saving, setSaving] = useState(false);
  const [viewOffer, setViewOffer] = useState(null);

  // newly picked image (uploaded on save); the saved image path stays in offer.img_url
  const [imageFile, setImageFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  // preview the picked file, else the image already saved on the offer (edit)
  const previewUrl = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : imageSrc(offer.img_url)),
    [imageFile, offer.img_url],
  );
  useEffect(() => {
    return () => {
      if (imageFile && previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [imageFile, previewUrl]);

  useEffect(() => {
    loadOffers();
  }, [pagingLimit, pageValue, appliedFilters]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadOffers = async () => {
    try {
      setLoading(true);
      start();
      const res = await offersService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setOfferRes({
          pagination: res.resData?.pagination || {},
          offers: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) setPageValue(n);
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handleStatusChange = async (row, newStatus) => {
    if (newStatus === row?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark ${row?.title} as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await offersService.updateStatus(row?.id, { status: newStatus });
      if (res?.success) toast.success(res.message);
      else toast.error(res?.message);
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadOffers();
    }
  };

  const handleDelete = async (row) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete offer?",
      text: `Are you sure you want to delete ${row?.title}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await offersService.delete(row?.id);
      if (res?.success) toast.success(res.message);
      else toast.error(res?.message);
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadOffers();
    }
  };

  const openAdd = () => {
    setOffer(initialOffer);
    resetImage();
    setEditId(null);
    setFormMode("add");
  };

  const openEdit = async (row) => {
    try {
      setLoading(true);
      const res = await offersService.details(row?.id);
      if (!res?.success || !res.resData) {
        toast.error(res?.message);
        return;
      }
      const o = res.resData;
      setOffer({
        title: o.title || "",
        promo_code: o.promo_code || "",
        description: o.description || "",
        terms_conditions: o.terms_conditions || "",
        img_url: o.img_url || "",
        discount_type: o.discount_type || "percentage",
        discount_value: o.discount_value ?? "",
        max_discount_amount: o.max_discount_amount ?? "",
        min_booking_amount: o.min_booking_amount ?? "",
        valid_from: toPickerDate(o.valid_from),
        valid_to: toPickerDate(o.valid_to),
        audience: o.audience || "all",
        display_order: String(o.display_order ?? 0),
        status: o.status || "active",
      });
      resetImage();
      setEditId(row?.id);
      setFormMode("edit");
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
    }
  };

  const openView = async (row) => {
    try {
      setLoading(true);
      const res = await offersService.details(row?.id);
      if (res?.success && res.resData) setViewOffer(res.resData);
      else toast.error(res?.message);
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetImage = () => {
    setImageFile(null);
    setFileInputKey((k) => k + 1);
  };

  const closeForm = () => {
    setFormMode(null);
    setEditId(null);
    setOffer(initialOffer);
    resetImage();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && !file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      e.target.value = "";
      setImageFile(null);
      return;
    }
    setImageFile(file);
  };

  const handleOfferChange = (e) => {
    setOffer({ ...offer, [e.target.name]: e.target.value });
  };

  const validateOffer = () => {
    // if (!offer.title.trim()) return "Title is required";
    // if (!offer.promo_code.trim()) return "Promo code is required";
    // if (!offer.terms_conditions.trim()) return "Terms & conditions are required";
    // if (offer.discount_value === "" || Number(offer.discount_value) <= 0)
    //   return "Discount value must be greater than 0";
    // if (offer.discount_type === "percentage" && Number(offer.discount_value) > 100)
    //   return "Percentage discount cannot be more than 100";
    // if (!offer.valid_from) return "Valid from date is required";
    // if (!offer.valid_to) return "Valid to date is required";    
    if (!offer.img_url && !imageFile && previewUrl) return "Banner image is required";
    if (!offer.display_order) return "Display order is required";
    return null;
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    const error = validateOffer();
    if (error) {
      toast.error(error);
      return;
    }
   
    const isEdit = formMode === "edit";
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update Banner?" : "Save Banner?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    let imgPath = offer.img_url || null;
    if (imageFile) {
      const uploadRes = await commonService.uploadFile(imageFile);
      const uploaded = uploadRes?.resData?.filesUploaded?.[0]?.path;
      if (!uploadRes?.success || !uploaded) {
        toast.error(uploadRes?.message || "Image upload failed");
        setSaving(false);
        return;
      }
      imgPath = uploaded;
    }

    const payload = {
      title: offer.title.trim(),
      description: offer.description.trim() || null,
      img_url: imgPath,
      terms_conditions: offer.terms_conditions.trim(),
      promo_code: offer.promo_code.trim().toUpperCase(),
      discount_type: offer.discount_type,
      discount_value: Number(offer.discount_value),
      max_discount_amount: toNumberOrNull(offer.max_discount_amount),
      min_booking_amount: toNumberOrNull(offer.min_booking_amount),
      valid_from: formatDateYYYYMMDD(offer.valid_from),
      valid_to: formatDateYYYYMMDD(offer.valid_to),
      audience: offer.audience,
      display_order: Number(offer.display_order) || 0,
      status: offer.status,
    };

    try {
      setSaving(true);
      const res = isEdit
        ? await offersService.update(editId, payload)
        : await offersService.create(payload);
      if (res?.success) {
        toast.success(res.message);
        closeForm();
        loadOffers();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setSaving(false);
    }
  };

  const offers = offerRes?.offers || [];
  const totalRecords = offerRes?.pagination?.total || 0;
  const totalPage = offerRes?.pagination?.totalPages || Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const activeCount = offers.filter((o) => o?.status === "active").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Offer Code", key: "offer_code" },
    { label: "Title", key: "title" },
    { label: "Promo Code", key: "promo_code" },
    { label: "Discount Type", key: "discount_type" },
    { label: "Discount Value", key: "discount_value" },
    { label: "Max Discount", key: "max_discount_amount" },
    { label: "Min Booking Amount", key: "min_booking_amount" },
    { label: "Valid From", key: "valid_from" },
    { label: "Valid To", key: "valid_to" },
    { label: "Created At", key: "created_at" },
    { label: "Audience", key: "audience" },
    { label: "Display Order", key: "display_order" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const thStyle = {
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    borderBottom: "2px solid #e8edf5",
    padding: "10px 12px",
    whiteSpace: "nowrap",
    background: "#f8f9fe",
  };

  const labelStyle = { fontSize: 12.5, fontWeight: 600, color: "#334155", marginBottom: 4 };
  const modalInput = { borderRadius: 8, fontSize: 13 };
  const modalField = (label, required, node, cls = "col-12 col-sm-6 col-md-3") => (
    <div className={cls}>
      <label className="form-label" style={labelStyle}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {node}
    </div>
  );
  const selectOf = (options, value, onChange, placeholder) => (
    <Select
      options={options}
      value={options.find((o) => o.value === value) || null}
      onChange={(o) => onChange(o ? o.value : "")}
      placeholder={placeholder}
      menuPortalTarget={document.body}
      styles={selectStyles}
    />
  );

  const actionBtn = (bg, color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    padding: 0,
  });

  const isEdit = formMode === "edit";

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>
              Home Banner
            </h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Home Banner</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Banner List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={openAdd}
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}
            >
              <i className="bi bi-plus-lg"></i> Add Banner
            </button>
            {/* <CSVLink
              data={offers}
              headers={csvHeaders}
              filename="offers.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink> */}
          </div>
        </div>

        {/* <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Search title, offer code or promo code"
                  value={filters.search}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={AUDIENCE_OPTIONS}
                  value={AUDIENCE_OPTIONS.find((o) => o.value === filters.audience) || null}
                  onChange={(o) => setFilters({ ...filters, audience: o ? o.value : "" })}
                  placeholder="Select Audience"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form> */}

        {/* <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Offers
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{activeCount}</b> Active on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{offers.length - activeCount}</b> Inactive / expired on this page
          </span>
        </div> */}

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  {/* <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Image", "Offer Code", "Title", "Promo Code", "Discount Type", "Discount Value", "Max Discount", "Min Booking", "Valid From", "Valid To", "Audience", "Order", "Status", "Created At", "Actions"].map((h, i) => (
                      <th key={i} style={thStyle}>
                        {h}
                      </th>
                    ))}
                  </tr> */}
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Image", "Display Order", "Status", "Created At", "Actions"].map((h, i) => (
                      <th key={i} style={thStyle}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {offers.map((row, idx) => (
                    <tr key={row?.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
                      <td className="ps-3" style={{ fontSize: 13 }}>
                        {(pageValue - 1) * Number(pagingLimit) + idx + 1}
                      </td>
                      <td>
                        {row?.img_url ? (
                          <a href={imageSrc(row.img_url)} target="_blank" rel="noopener noreferrer" title="Open image in new tab">
                            <img
                              src={imageSrc(row.img_url)}
                              alt={row?.title}
                              style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid #e8edf5", cursor: "pointer" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>                                         
                      <td style={{ fontSize: 13 }}>{row?.display_order}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row?.status}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          style={{
                            width: "auto",
                            minWidth: 0,
                            height: 28,
                            padding: "0 26px 0 10px",
                            backgroundPosition: "right 8px center",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: (STATUS_STYLES[row?.status] || STATUS_STYLES.inactive).bg,
                            color: (STATUS_STYLES[row?.status] || STATUS_STYLES.inactive).color,
                            border: "none",
                          }}
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ fontSize: 13 }}>{formatDate(row?.created_at)}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {row?.created_at ? moment(row.created_at).format("hh:mm A") : ""}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          {/* <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openView(row)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button> */}
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openEdit(row)}
                            title="Edit"
                            style={actionBtn("#e8f0fe", "var(--btn-bg)")}
                          >
                            <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: 13 }}></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDelete(row)}
                            title="Delete"
                            style={actionBtn("#fce8e6", "#d93025")}
                          >
                            <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: 13 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && offers.length === 0 && (
                    <tr>
                      <td colSpan={16} className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                        No offers found
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* <tbody>
                  {offers.map((row, idx) => (
                    <tr key={row?.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
                      <td className="ps-3" style={{ fontSize: 13 }}>
                        {(pageValue - 1) * Number(pagingLimit) + idx + 1}
                      </td>
                      <td>
                        {row?.img_url ? (
                          <a href={imageSrc(row.img_url)} target="_blank" rel="noopener noreferrer" title="Open image in new tab">
                            <img
                              src={imageSrc(row.img_url)}
                              alt={row?.title}
                              style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid #e8edf5", cursor: "pointer" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{row?.offer_code}</td>
                      <td>
                        <div className="fw-semibold text-dark" style={{ fontSize: 13, minWidth: 140 }}>
                          {row?.title}
                        </div>
                      </td>
                      <td>
                        <span className="fw-medium" style={{ fontSize: 13, fontFamily: "monospace" }}>
                          {row?.promo_code}
                        </span>
                      </td>
                      <td style={{ fontSize: 13 }}>{labelOf(row?.discount_type)}</td>
                      <td className="fw-semibold" style={{ fontSize: 13, whiteSpace: "nowrap" }}>{discountText(row)}</td>
                      <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{money(row?.max_discount_amount)}</td>
                      <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{money(row?.min_booking_amount)}</td>
                      <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{formatDate(row?.valid_from)}</td>
                      <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{formatDate(row?.valid_to)}</td>
                      <td>
                        <span className="badge rounded-pill" style={{ background: "#e7f5ff", color: "#1971c2", fontWeight: 600, fontSize: 11 }}>
                          {labelOf(row?.audience)}
                        </span>
                      </td>
                      <td style={{ fontSize: 13 }}>{row?.display_order}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row?.status}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          style={{
                            width: "auto",
                            minWidth: 0,
                            height: 28,
                            padding: "0 26px 0 10px",
                            backgroundPosition: "right 8px center",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: (STATUS_STYLES[row?.status] || STATUS_STYLES.inactive).bg,
                            color: (STATUS_STYLES[row?.status] || STATUS_STYLES.inactive).color,
                            border: "none",
                          }}
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ fontSize: 13 }}>{formatDate(row?.created_at)}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {row?.created_at ? moment(row.created_at).format("hh:mm A") : ""}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openView(row)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openEdit(row)}
                            title="Edit"
                            style={actionBtn("#e8f0fe", "var(--btn-bg)")}
                          >
                            <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: 13 }}></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDelete(row)}
                            title="Delete"
                            style={actionBtn("#fce8e6", "#d93025")}
                          >
                            <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: 13 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && offers.length === 0 && (
                    <tr>
                      <td colSpan={16} className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                        No offers found
                      </td>
                    </tr>
                  )}
                </tbody> */}
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> offers
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => setPageValue(1)}>
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => setPageValue(pageValue - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => setPageValue(n)}>
                      {n}
                    </button>
                  ),
                )}
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => setPageValue(pageValue + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => setPageValue(totalPage)}>
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit popup */}
        <Modal size="xl" centered show={!!formMode} onHide={closeForm}>
          <Modal.Header closeButton closeVariant="white">
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{isEdit ? "Update Banner" : "Add Banner"}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                {isEdit ? "Update banner information" : "Add a new banner to the system"}
              </div>
            </div>
          </Modal.Header>
          <form onSubmit={handleSave}>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="row g-2">   
              {modalField(
                  "Banner Image",
                  true,
                  <div className="d-flex align-items-center gap-3">
                    <input
                      key={fileInputKey}
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ ...modalInput, paddingTop: 8 }}
                    />
                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt="Banner Preview"
                        style={{ width: 64, height: 44, objectFit: "cover", borderRadius: 6, border: "1px solid #e8edf5", flexShrink: 0 }}
                      />
                    )}
                  </div>,
                  "col-12",
                )}
                {modalField(
                  "Display Order",
                  true,
                  <input type="number" min="0" className="form-control" name="display_order" value={offer.display_order} onChange={handleOfferChange} style={modalInput} />,
                )}
                {modalField("Status", true, selectOf(STATUS_OPTIONS, offer.status, (v) => setOffer({ ...offer, status: v }), "Select Status"))}                                
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button type="button" onClick={closeForm} className="btn btn-sm px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn btn-sm text-white px-3" style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                {saving ? "Saving..." : isEdit ? "Update Banner" : "Add Banner"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>
        {/* <Modal size="xl" centered show={!!formMode} onHide={closeForm}>
          <Modal.Header closeButton closeVariant="white">
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{isEdit ? "Update Offer" : "Add Offer"}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                {isEdit ? "Update offer information" : "Add a new offer to the system"}
              </div>
            </div>
          </Modal.Header>
          <form onSubmit={handleSave}>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="row g-2">
                {modalField(
                  "Title",
                  true,
                  <input type="text" className="form-control" name="title" value={offer.title} onChange={handleOfferChange} placeholder="Enter offer title" style={modalInput} />,
                  "col-12 col-md-6",
                )}
                {modalField(
                  "Promo Code",
                  true,
                  <input type="text" className="form-control" name="promo_code" value={offer.promo_code} onChange={handleOfferChange} placeholder="e.g. FLY100" style={{ ...modalInput, textTransform: "uppercase" }} />,
                )}
                {modalField("Audience", true, selectOf(AUDIENCE_OPTIONS, offer.audience, (v) => setOffer({ ...offer, audience: v }), "Select Audience"))}
                {modalField("Discount Type", true, selectOf(DISCOUNT_TYPE_OPTIONS, offer.discount_type, (v) => setOffer({ ...offer, discount_type: v }), "Select Discount Type"))}
                {modalField(
                  "Discount Value",
                  true,
                  <input type="number" min="0" className="form-control" name="discount_value" value={offer.discount_value} onChange={handleOfferChange} placeholder={offer.discount_type === "percentage" ? "e.g. 10" : "e.g. 500"} style={modalInput} />,
                )}
                {modalField(
                  "Max Discount Amount",
                  false,
                  <input type="number" min="0" className="form-control" name="max_discount_amount" value={offer.max_discount_amount} onChange={handleOfferChange} placeholder="Optional" style={modalInput} />,
                )}
                {modalField(
                  "Min Booking Amount",
                  false,
                  <input type="number" min="0" className="form-control" name="min_booking_amount" value={offer.min_booking_amount} onChange={handleOfferChange} placeholder="Optional" style={modalInput} />,
                )}
                {modalField(
                  "Valid From",
                  true,
                  <DatePicker
                    onOpenPickNewDate={false}
                    value={offer.valid_from || null}
                    onChange={(d) => setOffer({ ...offer, valid_from: d ? d.format("DD-MM-YYYY") : "" })}
                    format="DD-MM-YYYY"
                    placeholder="Select date"
                    maxDate={offer.valid_to || undefined}
                    editable={false}
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ ...modalInput, width: "100%" }}
                  />,
                )}
                {modalField(
                  "Valid To",
                  true,
                  <DatePicker
                    onOpenPickNewDate={false}
                    value={offer.valid_to || null}
                    onChange={(d) => setOffer({ ...offer, valid_to: d ? d.format("DD-MM-YYYY") : "" })}
                    format="DD-MM-YYYY"
                    placeholder="Select date"
                    minDate={offer.valid_from || undefined}
                    editable={false}
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ ...modalInput, width: "100%" }}
                  />,
                )}
                {modalField(
                  "Display Order",
                  false,
                  <input type="number" min="0" className="form-control" name="display_order" value={offer.display_order} onChange={handleOfferChange} style={modalInput} />,
                )}
                {modalField("Status", true, selectOf(STATUS_OPTIONS, offer.status, (v) => setOffer({ ...offer, status: v }), "Select Status"))}
                {modalField(
                  "Description",
                  false,
                  <textarea className="form-control" rows={2} name="description" value={offer.description} onChange={handleOfferChange} placeholder="Short description" style={modalInput} />,
                  "col-12 col-md-6",
                )}
                {modalField(
                  "Terms & Conditions",
                  true,
                  <textarea className="form-control" rows={2} name="terms_conditions" value={offer.terms_conditions} onChange={handleOfferChange} placeholder="Enter terms & conditions" style={modalInput} />,
                  "col-12 col-md-6",
                )}
                {modalField(
                  "Offer Image",
                  false,
                  <div className="d-flex align-items-center gap-3">
                    <input
                      key={fileInputKey}
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ ...modalInput, paddingTop: 8 }}
                    />
                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt="Offer"
                        style={{ width: 64, height: 44, objectFit: "cover", borderRadius: 6, border: "1px solid #e8edf5", flexShrink: 0 }}
                      />
                    )}
                  </div>,
                  "col-12",
                )}
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button type="button" onClick={closeForm} className="btn btn-sm px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn btn-sm text-white px-3" style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                {saving ? "Saving..." : isEdit ? "Update Offer" : "Add Offer"}
              </button>
            </Modal.Footer>
          </form>
        </Modal> */}

        {/* View popup */}
        <Modal size="lg" show={!!viewOffer} onHide={() => setViewOffer(null)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{ background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)", borderBottom: "none" }}
          >
            <Modal.Title className="d-flex align-items-center gap-2" style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Offer Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewOffer && (
              <DetailView
                fields={[
                  { label: "Title", value: viewOffer.title },
                  { label: "Offer Code", value: viewOffer.offer_code },
                  { label: "Promo Code", value: viewOffer.promo_code },
                  { label: "Audience", value: labelOf(viewOffer.audience) },
                  { label: "Discount Type", value: labelOf(viewOffer.discount_type) },
                  { label: "Discount Value", value: discountText(viewOffer) },
                  { label: "Max Discount Amount", value: viewOffer.max_discount_amount ? `₹ ${viewOffer.max_discount_amount}` : null },
                  { label: "Min Booking Amount", value: viewOffer.min_booking_amount ? `₹ ${viewOffer.min_booking_amount}` : null },
                  { label: "Valid From", value: formatDate(viewOffer.valid_from) },
                  { label: "Valid To", value: formatDate(viewOffer.valid_to) },
                  { label: "Display Order", value: viewOffer.display_order },
                  { label: "Status", value: labelOf(viewOffer.status), badge: true },
                  { label: "Created At", value: formatDateTime(viewOffer.created_at) },
                  { label: "Modified At", value: formatDateTime(viewOffer.modified_at) },
                  { label: "Image URL", value: imageSrc(viewOffer.img_url) || null, full: true },
                  { label: "Description", value: viewOffer.description, full: true },
                  { label: "Terms & Conditions", value: viewOffer.terms_conditions, full: true },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default Offers;
